// VR scene positioning constants
import {Vector3} from 'three';
import {DistanceSetting, RangeSetting} from '../types/exercises';

export const VR_POSITIONS = {
  CAMERA: {
    HEIGHT: 1.4,
  },

  // Lighting positions
  LIGHTS: {
    DIRECTIONAL: [10, 10, 5] as const,
  },

  // Exercise specific positions
  EXERCISE: {
    TARGET_HEIGHT: 1.4, // Standard height for targets (at eye level in viewer space)

    // Peripheral target positions (relative to main target)
    PERIPHERAL: {
      LEFT: new Vector3(-0.5, 0, 0),
      RIGHT: new Vector3(0.5, 0, 0),
    },

    // Distance settings
    DISTANCE: {
      NEAR: 0,
      MIDDLE: -1,
      FAR: -2,
    },

    // Range settings
    RANGE: {
      NARROW: 0.2,
      MEDIUM: 1,
      WIDE: 2,
    },

    // Speed settings
    SPEED: {
      SLOW: 0.2,
      NORMAL: 0.5,
      FAST: 0.8,
    },
  },
} as const;

// Helper functions to get position values based on settings
export const getSpeedValue = (speedSetting: number): number => {
  // Map speed value (0-15) to the speed range (0.3-0.8)
  const minSpeed = VR_POSITIONS.EXERCISE.SPEED.SLOW;
  const maxSpeed = VR_POSITIONS.EXERCISE.SPEED.FAST;
  return minSpeed + (speedSetting / 15) * (maxSpeed - minSpeed);
};

export const getRangeValue = (rangeSetting: RangeSetting): number => {
  switch (rangeSetting) {
    case 'narrow':
      return VR_POSITIONS.EXERCISE.RANGE.NARROW;
    case 'medium':
      return VR_POSITIONS.EXERCISE.RANGE.MEDIUM;
    case 'wide':
      return VR_POSITIONS.EXERCISE.RANGE.WIDE;
    default:
      return VR_POSITIONS.EXERCISE.RANGE.MEDIUM;
  }
};

export const getDistanceValue = (distanceSetting: DistanceSetting): number => {
  switch (distanceSetting) {
    case 'near':
      return VR_POSITIONS.EXERCISE.DISTANCE.NEAR;
    case 'middle':
      return VR_POSITIONS.EXERCISE.DISTANCE.MIDDLE;
    case 'far':
      return VR_POSITIONS.EXERCISE.DISTANCE.FAR;
    default:
      return VR_POSITIONS.EXERCISE.DISTANCE.MIDDLE;
  }
};

// Utility functions for common position calculations
export const createTargetPosition = (
  x: number,
  y: number = VR_POSITIONS.EXERCISE.TARGET_HEIGHT,
  z: number
): Vector3 => {
  return new Vector3(x, y, z);
};

export const createPeripheralPositions = (baseDistance: number): [Vector3, Vector3] => {
  const left = new Vector3().copy(VR_POSITIONS.EXERCISE.PERIPHERAL.LEFT);
  left.z = baseDistance;
  left.y = VR_POSITIONS.EXERCISE.TARGET_HEIGHT;

  const right = new Vector3().copy(VR_POSITIONS.EXERCISE.PERIPHERAL.RIGHT);
  right.z = baseDistance;
  right.y = VR_POSITIONS.EXERCISE.TARGET_HEIGHT;

  return [left, right];
};
